/**
 * Black Cherry / copy-paste AI pack — the archive-mode additions.
 *
 * The July bug: clips carried no video field and the preamble said "the video" (singular), so
 * the model believed it had ONE transcript and asked for "the other one". In archive mode every
 * pack now gets (1) a preamble that lists each video, its speakers and which speakers are LINKED
 * across videos, (2) per-clip `v` ("V1") and STRING ids ("6_456") with an explicit copy-exactly
 * rule, (3) `gap` (silence after) computed inside the clip's OWN video, never across videos.
 */

const fmtDur = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h) return `${h}h${String(m % 60).padStart(2, "0")}m`;
  return `${m}m${String(s % 60).padStart(2, "0")}s`;
};

/**
 * Clips for the pack. Rows are grouped by source (sheet order) and sorted by time WITHIN each
 * source; `gap` is the silence before the next clip of the SAME video.
 */
export function buildArchiveClips(rows) {
  const bySource = new Map();
  (rows || []).forEach((r) => {
    if (!r || r.space || !r.sentence) return;
    const k = r.source ? String(r.source.transcriptId) : "?";
    if (!bySource.has(k)) bySource.set(k, []);
    bySource.get(k).push(r);
  });
  const clips = [];
  bySource.forEach((list) => {
    list.sort((a, b) => (a.start_in || 0) - (b.start_in || 0));
    list.forEach((r, i) => {
      const next = list[i + 1];
      const dur = Math.max(0, (r.end_in ?? 0) - (r.start_in ?? 0));
      const gap = next ? Math.max(0, (next.start_in ?? 0) - (r.end_in ?? 0)) : 0;
      clips.push({
        id: String(r.id),
        v: r.source ? r.source.short : undefined,
        text: r.sentence,
        speaker: r.speaker || undefined,
        sentiment: r.sentiment || undefined,
        sec: Number(dur.toFixed(1)),
        gap: Number(gap.toFixed(1)),
      });
    });
  });
  return clips;
}

/** Speakers per source + which labels appear in more than one source (= linked people). */
export function archiveSpeakerSummary(sources, rows) {
  const perSource = new Map((sources || []).map((s) => [String(s.transcriptId), new Set()]));
  (rows || []).forEach((r) => {
    if (!r || r.space || !r.source || !r.speaker) return;
    const set = perSource.get(String(r.source.transcriptId));
    if (set) set.add(String(r.speaker));
  });
  const count = new Map();
  perSource.forEach((set) => set.forEach((label) => count.set(label, (count.get(label) || 0) + 1)));
  const linked = [...count.entries()].filter(([, n]) => n > 1).map(([label]) => label);
  return { perSource, linked };
}

/**
 * The preamble lines that tell the model it has SEVERAL videos. Inserted into every pack mode.
 * @param {Array} sources  archive sources (V1, V2 …)
 * @param {Array} rows     the merged rows AFTER speaker identities were applied
 */
export function archivePreambleLines(sources, rows) {
  const list = sources || [];
  const { perSource, linked } = archiveSpeakerSummary(list, rows);
  const lines = [
    `MULTI-VIDEO ARCHIVE — the clips below come from ${list.length} DIFFERENT videos combined into ONE sheet:`,
  ];
  list.forEach((s) => {
    const speakers = [...(perSource.get(String(s.transcriptId)) || [])];
    const who = speakers.length ? ` — speakers: ${speakers.join(", ")}` : "";
    lines.push(`• ${s.short} = "${s.label}" (${s.clips} clips, ${fmtDur(s.duration)})${who}`);
  });
  lines.push(
    'Every clip has "v" = which video it is from, and its "id" is a STRING like "6_456" (video-number_clip). Copy ids EXACTLY as shown, quotes included — never shorten, renumber or invent them.',
    'Timecodes restart in every video, so clips from different videos are NOT on one timeline; "gap" is measured inside a clip\'s own video only.',
  );
  if (linked.length) {
    lines.push(`SPEAKERS ARE LINKED ACROSS VIDEOS where the user said they are the same person: ${linked.map((l) => `"${l}"`).join(", ")} — the same real person wherever that name appears.`);
  }
  lines.push(
    'A label like "A·2" is speaker A of video 2, NOT linked to "A·1" — treat unlinked letters as different people unless the user says otherwise.',
    "You already have ALL the videos in front of you — never ask for another transcript. Cutting ACROSS videos is the whole point: combine clips from different videos freely; the app resolves each id to its own video on export.",
    "",
  );
  return lines;
}

/** Example ids for the prompt's JSON samples, as strings in archive mode. */
export function archiveExampleIds(clips, n = 3) {
  const ids = (clips || []).slice(0, n).map((c) => c.id);
  while (ids.length < n) ids.push(`1_${ids.length + 1}`);
  return ids;
}

/** Archive-only goal chips for the Black Cherry panel. */
export const ARCHIVE_GOAL_CHIPS = [
  { e: "🎞️", t: "Cross-cut the videos", p: "Cut ACROSS the videos: build one piece that weaves the strongest moments from every video together, alternating sources so it feels like one story told from several angles." },
  { e: "👤", t: "One speaker, every video", p: "Follow ONE speaker across all the videos: pick only that person's strongest lines from each video (use the linked name) and arrange them into a single coherent statement." },
];
